import { Response } from '../types/response';
import { CacheEntry, CacheStorage } from './cache-types';

/**
 * Local storage implementation of cache storage
 */
export class LocalCacheStorage implements CacheStorage {
  private keyPrefix: string;

  constructor(keyPrefix: string = 'cache') {
    this.keyPrefix = keyPrefix;
  }

  async get<T>(key: string): Promise<CacheEntry<T> | null> {
    try {
      const fullKey = `${this.keyPrefix}_${key}`;
      const stored = localStorage.getItem(fullKey);
      if (!stored) {
        return null;
      }
      return JSON.parse(stored) as CacheEntry<T>;
    } catch (error) {
      console.error(`Failed to get cache entry for key ${key}:`, error);
      return null;
    }
  }

  async set<T>(key: string, entry: CacheEntry<T>): Promise<void> {
    try {
      const fullKey = `${this.keyPrefix}_${key}`;
      localStorage.setItem(fullKey, JSON.stringify(entry));
    } catch (error) {
      console.error(`Failed to set cache entry for key ${key}:`, error);
    }
  }

  async remove(key: string): Promise<void> {
    try {
      const fullKey = `${this.keyPrefix}_${key}`;
      localStorage.removeItem(fullKey);
    } catch (error) {
      console.error(`Failed to remove cache entry for key ${key}:`, error);
    }
  }

  async clear(): Promise<void> {
    try {
      const keys = await this.getAllKeys();
      for (const key of keys) {
        await this.remove(key);
      }
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      const keys: string[] = [];
      const prefix = `${this.keyPrefix}_`;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix)) {
          keys.push(key.substring(prefix.length));
        }
      }
      
      return keys;
    } catch (error) {
      console.error('Failed to get cache keys:', error);
      return [];
    }
  }
}

/**
 * Memory-based cache storage (fallback when localStorage is not available)
 */
export class MemoryCacheStorage implements CacheStorage {
  private storage = new Map<string, CacheEntry<any>>();

  async get<T>(key: string): Promise<CacheEntry<T> | null> {
    return this.storage.get(key) || null;
  }

  async set<T>(key: string, entry: CacheEntry<T>): Promise<void> {
    this.storage.set(key, entry);
  }

  async remove(key: string): Promise<void> {
    this.storage.delete(key);
  }

  async clear(): Promise<void> {
    this.storage.clear();
  }

  async getAllKeys(): Promise<string[]> {
    return Array.from(this.storage.keys());
  }
}

/**
 * Helper functions for cache data operations
 */
export class CacheDataHelper {
  /**
   * Extract cache data from a cache storage instance
   */
  static async extractCacheData(storage: CacheStorage, cacheKey: string): Promise<Record<string, CacheEntry<any>>> {
    try {
      const keys = await storage.getAllKeys();
      const cacheData: Record<string, CacheEntry<any>> = {};
      
      for (const key of keys) {
        const entry = await storage.get(key);
        if (entry) {
          cacheData[key] = entry;
        }
      }
      
      return cacheData;
    } catch (error) {
      console.error(`Failed to extract cache data for ${cacheKey}:`, error);
      return {};
    }
  }

  /**
   * Load cache data into a cache storage instance
   */
  static async loadCacheData(storage: CacheStorage, cacheData: Record<string, CacheEntry<any>>): Promise<Response<void>> {
    try {
      for (const [key, entry] of Object.entries(cacheData)) {
        await storage.set(key, entry);
      }
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to load cache data: ${message}`);
    }
  }

  /**
   * Merge two cache data objects using latest-wins strategy
   */
  static mergeCacheData(
    existing: Record<string, CacheEntry<any>>,
    incoming: Record<string, CacheEntry<any>>
  ): Record<string, CacheEntry<any>> {
    const merged = { ...existing };
    
    for (const [key, incomingEntry] of Object.entries(incoming)) {
      const existingEntry = merged[key];
      
      if (!existingEntry || incomingEntry.timestamp > existingEntry.timestamp) {
        merged[key] = incomingEntry;
      }
    }
    
    return merged;
  }

  /**
   * Clean expired entries from cache data
   */
  static cleanExpiredEntries(cacheData: Record<string, CacheEntry<any>>): Record<string, CacheEntry<any>> {
    const now = Date.now();
    const cleaned: Record<string, CacheEntry<any>> = {};
    
    for (const [key, entry] of Object.entries(cacheData)) {
      if (entry.expiresAt > now) {
        cleaned[key] = entry;
      }
    }
    
    return cleaned;
  }
}

/**
 * Create appropriate cache storage based on environment
 */
export function createCacheStorage(keyPrefix: string): CacheStorage {
  try {
    // Test localStorage availability
    localStorage.setItem('_test', 'test');
    localStorage.removeItem('_test');
    return new LocalCacheStorage(keyPrefix);
  } catch (error) {
    console.warn('localStorage not available, using memory cache:', error);
    return new MemoryCacheStorage();
  }
}
